import {
  createNewUserEntityRole,
  deleteManyUserEntityRoles,
  deleteUserEntityRole,
  getAllEntityRolesByUserId,
  getEntityRoleByUserId,
} from '@/api/user-entity-role/user-entity-role';
import { TUserEntityRoleSchema } from '@/schemas/user-entity-role/user-entity-role.schema';
import { TCustomQueryProps } from '@/type/hooks.types';
import { ErrorResponse } from '@/utils/axios.http';
import { invalidateUserEntityRoles } from '@/utils/user-entity-role';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useCreateUserEntityRole = ({
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TUserEntityRoleSchema) => createNewUserEntityRole(data),
    onSuccess: (_, variables) => {
      invalidateUserEntityRoles(queryClient, variables);

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      console.error(error.error);
    },
  });
};

export const useGetAllEntityRolesByUserId = (userId: string) => {
  return useQuery({
    queryKey: ['getAllEntityRolesByUserId', userId],
    queryFn: () => getAllEntityRolesByUserId(userId),
    enabled: !!userId,
  });
};

export const useGetEntityRoleByUserId = (userId: string) => {
  return useQuery({
    queryKey: ['getEntityRoleByUserId', userId],
    queryFn: () => getEntityRoleByUserId(userId),
    enabled: !!userId,
  });
};

export const useGetUserRolesData = (userId: string) => {
  const { data: userEntityRoles, isLoading: isLoadingEntityRoles } =
    useGetAllEntityRolesByUserId(userId);

  const roles =
    !isLoadingEntityRoles &&
    userEntityRoles?.data?.map(
      (entityRole: TUserEntityRoleSchema) => entityRole?.roleType?.name
    );

  const userGroups =
    !isLoadingEntityRoles &&
    userEntityRoles?.data?.filter(
      (entityRole: TUserEntityRoleSchema) => entityRole.group
    );

  const userCompanies =
    !isLoadingEntityRoles &&
    userEntityRoles?.data?.filter(
      (entityRole: TUserEntityRoleSchema) => entityRole.company
    );

  const userBranches =
    !isLoadingEntityRoles &&
    userEntityRoles?.data?.filter(
      (entityRole: TUserEntityRoleSchema) => entityRole.branch
    );

  return {
    roles: roles ? [...new Set(roles)] : [],
    userGroups,
    userCompanies,
    userBranches,
  };
};

export const useDeleteUserEntityRole = ({
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TUserEntityRoleSchema) => deleteUserEntityRole(data),
    onSuccess: (_, variables) => {
      invalidateUserEntityRoles(queryClient, variables);

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      console.error(error.error);
    },
  });
};

export const useDeleteManyUserEntityRoles = ({
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TUserEntityRoleSchema[]) =>
      deleteManyUserEntityRoles(data),
    onSuccess: (_, variables) => {
      for (const item of variables) {
        invalidateUserEntityRoles(queryClient, item);
      }

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      console.error(error.error);
    },
  });
};
